/*
  base-ed.js: behaviour for the editorial look (base-ed-* sections).
  Drawers (menu, filters), the header that turns sticky, the search panel with live results,
  folding rows, the rotating announcement, swipe rows with dots, collection filters, the product
  gallery with zoom, variants and add to cart, recommendations and the rise-in on scroll.
  No dependencies.
*/
(() => {
  const html = document.documentElement;
  const overlay = () => document.querySelector('[data-ed-overlay]');
  let openDrawer = null;
  let lastTrigger = null;

  function open(id, trigger) {
    const el = document.getElementById(id);
    if (!el) return;
    if (openDrawer && openDrawer !== el) close();
    closeSearch();
    openDrawer = el;
    lastTrigger = trigger || null;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    overlay()?.classList.add('is-visible');
    html.classList.add('ed-locked');
    setTimeout(() => el.focus({ preventScroll: true }), 50);
  }

  function close() {
    if (!openDrawer) return;
    const el = openDrawer;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (lastTrigger) {
      lastTrigger.setAttribute('aria-expanded', 'false');
      lastTrigger.focus({ preventScroll: true });
    }
    overlay()?.classList.remove('is-visible');
    html.classList.remove('ed-locked');
    openDrawer = null;
  }

  /* Folding rows: menu levels, footer columns, FAQ, filter groups */
  function toggleFold(wrap) {
    const body = [...wrap.children].find((c) => c.matches('[data-ed-fold-body]'));
    const btn = wrap.querySelector('[data-ed-fold-toggle]');
    if (!body) return;
    const opening = !wrap.classList.contains('is-open');
    body.style.height = (opening ? 0 : body.scrollHeight) + 'px';
    if (!opening) body.offsetHeight; // commit the start height
    requestAnimationFrame(() => {
      wrap.classList.toggle('is-open', opening);
      body.style.height = (opening ? body.scrollHeight : 0) + 'px';
    });
    body.addEventListener('transitionend', () => { body.style.height = ''; }, { once: true });
    if (btn) btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-ed-open]');
    if (opener) {
      e.preventDefault();
      open(opener.dataset.edOpen, opener);
      return;
    }
    if (e.target.closest('[data-ed-close]') || e.target.matches('[data-ed-overlay]')) {
      e.preventDefault();
      close();
      closeSearch();
      return;
    }
    const fold = e.target.closest('[data-ed-fold-toggle]');
    if (fold) {
      e.preventDefault();
      toggleFold(fold.closest('[data-ed-fold]'));
    }
  });
  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape') { close(); closeSearch(); closeZoom(); closeQuickView(); }
  });
  // Cart drawer: minus at quantity 1 removes the line (Dawn stops at the minimum instead).
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('.ed-cart .ed-qty__btn[name="minus"]');
    if (!minus) return;
    const input = minus.parentElement.querySelector('.ed-qty__input');
    const items = minus.closest('cart-drawer-items');
    if (!input || !items || parseInt(input.value, 10) > 1) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    items.updateQuantity(input.dataset.index, 0, e);
  }, true);
  window.edDrawer = { open, close };

  /* Header: transparent over the hero, fixed and white once scrolled past */
  function initHeader() {
    const wrap = document.querySelector('[data-ed-header-wrap]');
    if (!wrap || wrap.dataset.edReady) return;
    wrap.dataset.edReady = '1';
    const header = wrap.querySelector('[data-ed-header]');
    const isOverlay = wrap.classList.contains('ed-header-wrap--overlay');
    let sticky = false;
    let base = 0;
    const measure = () => {
      if (sticky) return;
      base = header.offsetHeight;
      if (!isOverlay) wrap.style.minHeight = base + 'px';
    };
    const top = () => wrap.getBoundingClientRect().top + window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (!sticky && y > top() + base) {
        sticky = true;
        header.classList.add('is-sticky');
        header.classList.remove('is-light');
      } else if (sticky && y <= top()) {
        sticky = false;
        header.classList.remove('is-sticky');
        if (isOverlay) header.classList.add('is-light');
      }
    };
    measure();
    window.addEventListener('resize', () => { measure(); onScroll(); });
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* Search panel with live results */
  let searchTimer = null;
  let searchCtrl = null;
  function closeSearch() {
    const panel = document.querySelector('[data-ed-search]');
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    html.classList.remove('ed-search-open');
    if (!openDrawer) {
      overlay()?.classList.remove('is-visible');
      html.classList.remove('ed-locked');
    }
  }
  function initSearch() {
    const panel = document.querySelector('[data-ed-search]');
    if (!panel || panel.dataset.edReady) return;
    panel.dataset.edReady = '1';
    const input = panel.querySelector('[data-ed-search-input]');
    const results = panel.querySelector('[data-ed-search-results]');
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-ed-search-open]')) {
        e.preventDefault();
        close();
        panel.classList.add('is-open');
        panel.setAttribute('aria-hidden', 'false');
        html.classList.add('ed-search-open');
        overlay()?.classList.add('is-visible');
        setTimeout(() => input.focus(), 60);
        return;
      }
      if (e.target.closest('[data-ed-search-close]')) {
        e.preventDefault();
        closeSearch();
      }
    });
    input.addEventListener('input', () => {
      clearTimeout(searchTimer);
      const q = input.value.trim();
      if (!q) { results.hidden = true; results.innerHTML = ''; return; }
      searchTimer = setTimeout(async () => {
        searchCtrl?.abort();
        searchCtrl = new AbortController();
        const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
        const url = base + '?q=' + encodeURIComponent(q) + '&section_id=base-ed-predictive-search&resources[type]=product,collection,query,page&resources[limit]=4&resources[limit_scope]=each';
        try {
          const res = await fetch(url, { signal: searchCtrl.signal });
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const box = doc.querySelector('[data-ed-psearch]');
          results.innerHTML = box ? box.outerHTML : '';
          results.hidden = !box;
        } catch (err) {}
      }, 200);
    });
  }

  /* Announcement and rotating icons: fade between items */
  function initAnnouncements(root = document) {
    root.querySelectorAll('[data-ed-rotate]').forEach((track) => {
      if (track.dataset.edReady) return;
      track.dataset.edReady = '1';
      const items = [...track.children];
      if (items.length < 2) return;
      const dots = [...(track.parentElement.querySelector('[data-ed-rotate-dots]')?.children || [])];
      let i = 0;
      setInterval(() => {
        items[i].classList.remove('is-active');
        dots[i]?.classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
        dots[i]?.classList.add('is-active');
      }, (parseInt(track.dataset.edRotate, 10) || 5) * 1000);
    });
  }

  /* Product info tabs: one panel at a time, arrow keys move between tabs */
  function initTabs(root = document) {
    root.querySelectorAll('[data-ed-tabs]').forEach((box) => {
      if (box.dataset.edReady) return;
      box.dataset.edReady = '1';
      const tabs = [...box.querySelectorAll('[role="tab"]')];
      const show = (tab) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          const panel = box.querySelector('#' + CSS.escape(t.getAttribute('aria-controls')));
          if (panel) panel.hidden = !on;
        });
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => show(t));
        t.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
          show(next); next.focus();
        });
      });
    });
  }

  /* Swipe rows with dots underneath */
  function initDots(root = document) {
    root.querySelectorAll('[data-ed-dots]').forEach((track) => {
      if (track.dataset.edReady) return;
      track.dataset.edReady = '1';
      const holder = track.closest('.ed-page') || track.parentElement;
      const dots = [...(holder.querySelector('[data-ed-dots-for]')?.children || [])];
      if (!dots.length) return;
      const sync = () => {
        const items = track.children;
        const pitch = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : track.clientWidth;
        let i = Math.round(track.scrollLeft / (pitch || 1));
        if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) i = dots.length - 1;
        dots.forEach((d, k) => d.classList.toggle('is-active', k === Math.min(i, dots.length - 1)));
      };
      track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    });
  }

  /* Carousels: a scroll-snapping track with prev/next buttons */
  function initCarousels(root = document) {
    root.querySelectorAll('[data-ed-carousel]').forEach((wrap) => {
      if (wrap.dataset.edCarousel) return;
      wrap.dataset.edCarousel = '1';
      const track = wrap.querySelector('[data-ed-track]');
      const prev = wrap.querySelector('[data-ed-prev]');
      const next = wrap.querySelector('[data-ed-next]');
      if (!track) return;
      const pitch = () => {
        const items = track.children;
        return items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        wrap.classList.toggle('ed-carousel--static', max <= 0);
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -pitch(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: pitch(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* Collection & search: filters, sort, pages, live updates */
  function initCollection(root = document) {
    root.querySelectorAll('[data-ed-collection]').forEach((wrap) => {
      if (wrap.dataset.edReady) return;
      wrap.dataset.edReady = '1';
      const sectionId = wrap.dataset.section;
      const filters = wrap.querySelector('[data-ed-filters]');
      if (filters) document.body.appendChild(filters); // out of the section, so it sits above the page

      wrap.addEventListener('click', (e) => {
        const t = e.target.closest('[data-ed-filter-toggle]');
        if (t && filters) { open(filters.id, t); return; }
        const pageLink = e.target.closest('.ed-pagination a');
        if (pageLink) { e.preventDefault(); load(pageLink.href, true); }
      });
      filters?.addEventListener('click', (e) => {
        if (e.target.closest('[data-ed-filter-close]')) close();
      });
      wrap.querySelector('[data-ed-sort-select]')?.addEventListener('change', (e) => {
        const hidden = filters?.querySelector('[data-ed-sort-hidden]');
        if (hidden) hidden.value = e.target.value;
        update(e.target.value);
      });

      let timer = null;
      filters?.addEventListener('input', (e) => {
        const range = e.target.closest('[data-ed-price-range]');
        if (range) syncRange(range, e.target);
      });
      filters?.addEventListener('change', (e) => {
        if (!e.target.closest('[data-ed-facets-form]')) return;
        clearTimeout(timer);
        timer = setTimeout(update, e.target.type === 'number' || e.target.type === 'range' ? 500 : 0);
      });
      filters?.addEventListener('submit', (e) => {
        if (e.target.matches('[data-ed-facets-form]')) { e.preventDefault(); update(); }
      });

      function syncRange(range, source) {
        const max = parseFloat(range.dataset.max) || 0;
        const minIn = range.querySelector('[data-ed-min]');
        const maxIn = range.querySelector('[data-ed-max]');
        const minR = range.querySelector('[data-ed-range-min]');
        const maxR = range.querySelector('[data-ed-range-max]');
        if (source === minR) minIn.value = Math.min(minR.value, maxR.value);
        if (source === maxR) maxIn.value = Math.max(maxR.value, minR.value);
        if (source === minIn) minR.value = minIn.value;
        if (source === maxIn) maxR.value = maxIn.value;
        const a = Math.min(minR.value, maxR.value), b = Math.max(minR.value, maxR.value);
        const fill = range.querySelector('[data-ed-fill]');
        if (fill && max) { fill.style.left = (a / max) * 100 + '%'; fill.style.right = (100 - (b / max) * 100) + '%'; }
      }
      filters?.querySelectorAll('[data-ed-price-range]').forEach((r) => syncRange(r));

      function params(sort) {
        const out = new URLSearchParams();
        const form = filters?.querySelector('[data-ed-facets-form]');
        if (form) {
          for (const [k, v] of new FormData(form).entries()) if (v !== '') out.append(k, v);
          form.querySelectorAll('[data-ed-price-range]').forEach((r) => {
            const minIn = r.querySelector('[data-ed-min]');
            const maxIn = r.querySelector('[data-ed-max]');
            if (parseFloat(minIn.value) <= 0) out.delete(minIn.name);
            if (parseFloat(maxIn.value) >= parseFloat(r.dataset.max)) out.delete(maxIn.name);
          });
        } else {
          const cur = new URLSearchParams(location.search);
          cur.forEach((v, k) => { if (k !== 'page' && k !== 'sort_by') out.append(k, v); });
        }
        if (sort) out.set('sort_by', sort);
        return out.toString();
      }
      function update(sort) {
        load(location.pathname + '?' + params(sort), false);
      }
      async function load(url, scroll) {
        const u = new URL(url, location.origin);
        u.searchParams.set('section_id', sectionId);
        wrap.classList.add('is-loading');
        try {
          const res = await fetch(u.toString());
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = doc.querySelector('[data-ed-collection]');
          if (!fresh) return;
          ['[data-ed-results]', '[data-ed-count]'].forEach((sel) => {
            const a = wrap.querySelector(sel), b = fresh.querySelector(sel);
            if (a && b) a.innerHTML = b.innerHTML;
          });
          const facets = filters?.querySelector('[data-ed-facets]');
          const freshFacets = fresh.querySelector('[data-ed-facets]');
          if (facets && freshFacets) {
            const openIdx = [...facets.querySelectorAll('.ed-facet')].map((f) => f.classList.contains('is-open'));
            facets.innerHTML = freshFacets.innerHTML;
            facets.querySelectorAll('.ed-facet').forEach((f, i) => {
              if (openIdx[i]) { f.classList.add('is-open'); f.querySelector('[data-ed-fold-toggle]')?.setAttribute('aria-expanded', 'true'); }
            });
            facets.querySelectorAll('[data-ed-price-range]').forEach((r) => syncRange(r));
          }
          u.searchParams.delete('section_id');
          history.replaceState({}, '', u.pathname + (u.search || ''));
          initReveal(wrap);
          if (scroll) wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } finally {
          wrap.classList.remove('is-loading');
        }
      }
    });
  }

  /* Photo zoom */
  let zoomBox = null;
  function closeZoom() {
    if (!zoomBox || !zoomBox.classList.contains('is-open')) return;
    zoomBox.classList.remove('is-open');
    zoomBox.setAttribute('aria-hidden', 'true');
    html.classList.remove('ed-locked');
  }

  /* Product page: gallery, variants, add to cart */
  function initProduct(root = document) {
    root.querySelectorAll('[data-ed-product]').forEach((wrap) => {
      if (wrap.dataset.edReady) return;
      wrap.dataset.edReady = '1';
      const data = JSON.parse(wrap.querySelector('[data-ed-product-json]')?.textContent || '{}');
      const track = wrap.querySelector('[data-ed-ptrack]');
      const slides = [...wrap.querySelectorAll('[data-ed-pslide]')];
      const thumbs = [...wrap.querySelectorAll('[data-ed-thumb]')];
      const dots = [...wrap.querySelectorAll('.ed-gallery__dot')];
      // Phone: the photos run in a loop like the design. A copy of the last photo sits
      // before the first and a copy of the first after the last, so both neighbours peek in;
      // landing on a copy jumps (unseen) to the real photo. The copies are hidden on a computer.
      let clones = [];
      if (track && slides.length > 1) {
        const mk = (src) => {
          const c = src.cloneNode(true);
          c.removeAttribute('data-ed-pslide');
          c.setAttribute('data-ed-clone', '');
          c.setAttribute('aria-hidden', 'true');
          c.tabIndex = -1;
          c.querySelectorAll('img').forEach((im) => { im.loading = 'lazy'; im.removeAttribute('fetchpriority'); });
          return c;
        };
        const before = mk(slides[slides.length - 1]);
        const after = mk(slides[0]);
        track.insertBefore(before, slides[0]);
        track.appendChild(after);
        clones = [before, after];
      }
      const looping = () => clones.length > 0 && clones[0].offsetParent !== null;
      const step = () => (slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth) || 1;
      const raw = () => (track ? Math.round(track.scrollLeft / step()) : 0);
      const index = () => {
        const n = slides.length;
        const i = raw() - (looping() ? 1 : 0);
        return ((i % n) + n) % n;
      };
      const goTo = (i, smooth = true) => {
        if (!track) return;
        const n = Math.max(0, Math.min(i, slides.length - 1)) + (looping() ? 1 : 0);
        track.scrollTo({ left: n * step(), behavior: smooth ? 'smooth' : 'auto' });
      };
      const jump = (to) => {
        track.style.scrollSnapType = 'none';
        track.scrollLeft = to * step();
        requestAnimationFrame(() => { track.style.scrollSnapType = ''; });
      };
      const settle = () => {
        if (!looping()) return;
        const r = raw();
        if (r <= 0) jump(slides.length);
        else if (r >= slides.length + 1) jump(1);
      };
      const sync = () => {
        const i = index();
        // Only the photo in the middle carries the zoom button
        if (track) {
          const r = raw();
          [...track.children].forEach((c, k) => c.classList.toggle('is-current', k === r));
        }
        thumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
        dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      };
      if (track) {
        let t = null;
        track.addEventListener('scroll', () => {
          requestAnimationFrame(sync);
          clearTimeout(t);
          t = setTimeout(settle, 140);
        }, { passive: true });
        const place = () => { if (looping() && raw() === 0) jump(1); };
        place();
        sync();
        window.addEventListener('resize', () => { if (!looping()) track.scrollLeft = 0; place(); });
      }
      thumbs.forEach((t) => t.addEventListener('click', () => goTo(parseInt(t.dataset.edThumb, 10))));

      // Zoom: a white full-screen layer with the photos side by side
      const zoom = wrap.querySelector('[data-ed-zoom-box]');
      if (zoom) {
        document.body.appendChild(zoom);
        zoomBox = zoom;
        const zt = zoom.querySelector('[data-ed-zoom-track]');
        [...slides, ...clones].forEach((s) => s.addEventListener('click', () => {
          zoom.classList.add('is-open');
          zoom.setAttribute('aria-hidden', 'false');
          html.classList.add('ed-locked');
          requestAnimationFrame(() => { zt.scrollLeft = parseInt(s.dataset.edZoom, 10) * zt.clientWidth; });
        }));
        zoom.querySelector('[data-ed-zoom-close]')?.addEventListener('click', closeZoom);
        zoom.querySelector('[data-ed-zoom-prev]')?.addEventListener('click', () => zt.scrollBy({ left: -zt.clientWidth, behavior: 'smooth' }));
        zoom.querySelector('[data-ed-zoom-next]')?.addEventListener('click', () => zt.scrollBy({ left: zt.clientWidth, behavior: 'smooth' }));
      }

      const form = wrap.querySelector('[data-ed-pform]');
      if (!form || !data.variants) return;
      const idInput = form.querySelector('[data-ed-variant-id]');
      const atc = form.querySelector('[data-ed-atc]');
      const atcText = form.querySelector('[data-ed-atc-text]');
      const price = wrap.querySelector('[data-ed-price]');

      const current = () => {
        const chosen = [...wrap.querySelectorAll('[data-ed-option]')].map((f) => f.querySelector('input:checked')?.value);
        return data.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      };
      form.addEventListener('change', (e) => {
        const input = e.target.closest('[data-ed-option-input]');
        if (!input) return;
        const valueEl = input.closest('[data-ed-option]').querySelector('[data-ed-option-value]');
        if (valueEl) valueEl.textContent = input.value;
        const v = current();
        const stock = wrap.querySelector('[data-ed-stock]');
        if (stock) stock.hidden = !(v && v.available);
        if (!v) {
          atc.disabled = true;
          atcText.textContent = data.labels.unavailable;
          return;
        }
        idInput.value = v.id;
        atc.disabled = !v.available;
        atcText.textContent = v.available ? data.labels.add : data.labels.sold;
        const priceHtml = '<span class="ed-price__now">' + v.price + '</span>';
        price.innerHTML = priceHtml;
        const sp = wrap.querySelector('[data-ed-sticky-price]');
        if (sp) sp.innerHTML = priceHtml;
        const ss = wrap.querySelector('[data-ed-sticky-select]');
        if (ss) ss.value = String(v.id);
        const sa = wrap.querySelector('[data-ed-sticky-add]');
        if (sa) sa.disabled = !v.available;
        if (v.media) {
          const k = slides.findIndex((sl) => sl.dataset.mediaId === String(v.media));
          if (k > -1) goTo(k);
        }
        const url = new URL(location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url.toString());
      });

      form.addEventListener('submit', (e) => {
        const cart = document.querySelector('cart-drawer');
        if (!cart || !cart.renderContents) return;
        e.preventDefault();
        atc.classList.add('is-loading');
        const fd = new FormData(form);
        if (cart.getSectionsToRender) {
          fd.append('sections', cart.getSectionsToRender().map((s) => s.id));
          fd.append('sections_url', location.pathname);
        }
        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        })
          .then((r) => r.json())
          .then((res) => {
            if (res.status) return;
            cart.renderContents(res);
            cart.classList.remove('is-empty');
            cart.open && cart.open();
          })
          .catch(() => {})
          .finally(() => atc.classList.remove('is-loading'));
      });

      // Add-to-cart bar at the bottom: shows once the main button has scrolled out of view
      const sticky = wrap.querySelector('[data-ed-sticky]');
      if (sticky) {
        document.body.appendChild(sticky);
        const select = sticky.querySelector('[data-ed-sticky-select]');
        select?.addEventListener('change', () => {
          const v = data.variants.find((x) => String(x.id) === select.value);
          if (!v) return;
          v.options.forEach((val, i) => {
            const input = [...wrap.querySelectorAll('[data-ed-option-input="' + i + '"]')].find((inp) => inp.value === val);
            if (input && !input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
          });
        });
        sticky.querySelector('[data-ed-sticky-add]')?.addEventListener('click', () => {
          if (form.requestSubmit) form.requestSubmit(); else atc.click();
        });
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(([en]) => {
            const show = !en.isIntersecting && en.boundingClientRect.top < 0;
            sticky.classList.toggle('is-visible', show);
            sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          }).observe(atc);
        }
      }
    });
  }

  /* Quick view: the product section of the product page, loaded into a large white window */
  let qvBox = null;
  function closeQuickView() {
    if (!qvBox || !qvBox.classList.contains('is-open')) return;
    qvBox.classList.remove('is-open');
    qvBox.setAttribute('aria-hidden', 'true');
    html.classList.remove('ed-locked');
  }
  async function openQuickView(url) {
    if (!qvBox) {
      qvBox = document.createElement('div');
      qvBox.className = 'ed-qv';
      qvBox.setAttribute('role', 'dialog');
      qvBox.setAttribute('aria-modal', 'true');
      qvBox.innerHTML = '<div class="ed-qv__shade" data-ed-qv-close></div><div class="ed-qv__panel"><button type="button" class="ed-qv__close" data-ed-qv-close aria-label="Close"><svg class="ed-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg></button><div class="ed-qv__body" data-ed-qv-body></div></div>';
      document.body.appendChild(qvBox);
      qvBox.addEventListener('click', (e) => { if (e.target.closest('[data-ed-qv-close]')) closeQuickView(); });
    }
    const body = qvBox.querySelector('[data-ed-qv-body]');
    body.innerHTML = '<div class="ed-qv__loading"></div>';
    qvBox.classList.add('is-open');
    qvBox.setAttribute('aria-hidden', 'false');
    html.classList.add('ed-locked');
    try {
      const res = await fetch(url);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const prod = doc.querySelector('[data-ed-product]');
      if (!prod) { location.href = url; return; }
      prod.querySelectorAll('[data-ed-sticky], [data-ed-zoom-box], script[type="application/ld+json"]').forEach((n) => n.remove());
      prod.querySelectorAll('[id]').forEach((n) => { n.id = 'qv-' + n.id; });
      prod.querySelectorAll('label[for]').forEach((n) => { n.htmlFor = 'qv-' + n.htmlFor; });
      prod.querySelectorAll('[data-ed-ready]').forEach((n) => n.removeAttribute('data-ed-ready'));
      prod.removeAttribute('data-ed-ready');
      prod.classList.add('ed-product--quick');
      body.innerHTML = '';
      body.appendChild(prod);
      initAll(body);
    } catch (err) {
      location.href = url;
    }
  }
  document.addEventListener('click', (e) => {
    const q = e.target.closest('[data-ed-quickview]');
    if (!q) return;
    e.preventDefault();
    e.stopPropagation();
    openQuickView(q.dataset.edQuickview);
  }, true);
  document.addEventListener('keydown', (e) => {
    const q = e.target.closest && e.target.closest('[data-ed-quickview]');
    if (q && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openQuickView(q.dataset.edQuickview); }
  });
  // After adding from the quick view, close it so the cart drawer is in view
  document.addEventListener('submit', (e) => { if (e.target.closest('.ed-qv')) setTimeout(closeQuickView, 400); });

  /* Recommendations: fetched after load, like Dawn */
  function initRelated(root = document) {
    root.querySelectorAll('[data-ed-related]').forEach(async (el) => {
      if (el.dataset.edReady || el.querySelector('.ed-pgrid__track')) return;
      el.dataset.edReady = '1';
      try {
        const res = await fetch(el.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('[data-ed-related]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          initAll(el);
        }
      } catch (e) {}
    });
  }

  /* Rise in on scroll */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('is-in');
            io.unobserve(en.target);
          }
        });
      }, { rootMargin: '0px 0px -60px 0px' })
    : null;
  function initReveal(root = document) {
    root.querySelectorAll('.ed-rise:not(.is-in)').forEach((el) => {
      if (io) io.observe(el);
      else el.classList.add('is-in');
    });
  }

  /* Boxes on the product page: height + fade */
  function initAccordions(root = document) {
    root.querySelectorAll('details[data-ed-accordion]').forEach((d) => {
      if (d.dataset.edReady) return;
      d.dataset.edReady = '1';
      const summary = d.querySelector('summary');
      const body = d.querySelector('[data-ed-accordion-body]');
      if (!summary || !body) return;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (d.open) {
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(() => { body.style.height = '0px'; body.style.opacity = '0'; });
          body.addEventListener('transitionend', () => { d.open = false; body.style.height = ''; body.style.opacity = ''; }, { once: true });
        } else {
          d.open = true;
          const h = body.scrollHeight;
          body.style.height = '0px';
          body.style.opacity = '0';
          requestAnimationFrame(() => { body.style.height = h + 'px'; body.style.opacity = '1'; });
          body.addEventListener('transitionend', () => { body.style.height = ''; body.style.opacity = ''; }, { once: true });
        }
      });
    });
  }

  function initAll(root) {
    initAnnouncements(root);
    initTabs(root);
    initDots(root);
    initCarousels(root);
    initCollection(root);
    initProduct(root);
    initRelated(root);
    initReveal(root);
    initAccordions(root);
  }

  const start = () => { initHeader(); initSearch(); initAll(document); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (e) => { initHeader(); initSearch(); initAll(e.target); });
  window.edInit = initAll;
})();
